import { ReactElement, useState, useCallback, createContext, useContext } from 'react';
import { DriveOnTargetDto as Target, DriveOnDisplayableTaskDto as DriveonTask } from '@/generated/model';
import { DriveOnDriverInputInputType as InputType } from '@/generated/model/driveOnDriverInputInputType';
import { Logbook } from '@/model/Logbook';

export type DriveOnContext = {
  target: Target | undefined;
  setTarget: (target: Target | undefined) => void;
  task: DriveonTask | undefined;
  setTask: (task: DriveonTask | undefined) => void;
  logbook: Logbook | undefined;
  setLogbook: (logbook: Logbook | undefined) => void;
  inputType: InputType | undefined;
  setInputType: (inputType: InputType | undefined) => void;
};

type DriveOnContextProviderProps = {
  children: ReactElement;
};

export const ctxDriveOn = createContext<DriveOnContext | undefined>(undefined);

export function DriveOnProvider({ children }: DriveOnContextProviderProps) {
  const [task, setTaskState] = useState<DriveonTask | undefined>(undefined);
  const [target, setTargetState] = useState<Target | undefined>(undefined);
  const [logbook, setLogbookState] = useState<Logbook | undefined>(undefined);
  const [inputType, setInputTypeState] = useState<InputType | undefined>(undefined);

  const setInputType = useCallback((newInputType: InputType | undefined) => {
    setInputTypeState(newInputType);
  }, []);

  const setTask = useCallback((newTask: DriveonTask | undefined) => {
    setTaskState(newTask);
  }, []);

  const setTarget = useCallback((newTarget: Target | undefined) => {
    setTargetState(newTarget);
  }, []);

  const setLogbook = useCallback((newLogbook: Logbook | undefined) => {
    setLogbookState(newLogbook);
  }, []);

  return (
    <ctxDriveOn.Provider
      value={{
        task,
        target,
        logbook,
        inputType,
        setTask,
        setTarget,
        setLogbook,
        setInputType,
      }}
    >
      {children}
    </ctxDriveOn.Provider>
  );
}

export function useDriveOnContext() {
  const context = useContext(ctxDriveOn);
  if (!context) {
    throw new Error('DriveOn context must be used within a Provider');
  }
  return context;
}